import React from "react";
import { m, useReducedMotion } from "framer-motion";
import {
  ArrowRight, Languages, Mic, NotebookPen, ShieldCheck, Wand2,
} from "lucide-react";
import { useMarketingMotion } from "../../components/marketing/MarketingMotion";
import VoiceAiraProductFilm from "./VoiceAiraProductFilm";

/* Keep these fallbacks byte-for-byte aligned with backend/app/core/marketing.py DEFAULTS["voice_ai"]. */
export const VOICE_AI_FALLBACK = {
  badge: "Voice intelligence",
  heading: "Speak to your school software —",
  highlight: "in your language",
  subhead: "Aira understands spoken English and ten Indian languages, detects the language automatically, tidies your words into a clear request, and reads the answer back in the language you spoke. Meetings become structured notes with decisions and follow-ups — typed by no one.",
  cards: [
    { title: "Automatic language detection", body: "Speak Hindi, Tamil, Bengali, Telugu, Odia or English — Aira detects the language, shows you what it heard, and replies in the same language." },
    { title: "Speech becomes a clean request", body: "“Uh, can you, like, tell me how many students absent today?” becomes “How many students are absent today?” — fillers removed, meaning untouched." },
    { title: "AI meeting notes", body: "Start from any page in the app: record a parent-teacher meeting or an admissions call (with consent) and get structured notes — summary, decisions, action items and follow-up dates — linked to the student or lead." },
    { title: "Private by design", body: "Audio is transcribed and discarded — only the reviewed transcript and notes are kept. Personal details are scrubbed before any text reaches an AI model." },
  ],
  footnote: "Included with the Aira AI suite on your plan — no separate app, no extra subscription. Voice features activate when the school's voice provider is configured. Aira proposes — a person confirms.",
};

const EASE = [0.22, 1, 0.36, 1];
const CARD_ICONS = [Languages, Wand2, NotebookPen, ShieldCheck];
const CARD_LAYOUTS = [
  "lg:col-span-5",
  "lg:col-span-7",
  "lg:col-span-7",
  "lg:col-span-5",
];

function FadeUp({ reduce, className, delay = 0, children }) {
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <m.div
      className={className}
      initial={{ opacity: 0, transform: "translateY(22px)" }}
      whileInView={{ opacity: 1, transform: "translateY(0px)" }}
      viewport={{ once: true, amount: 0.22 }}
      transition={{ duration: 0.5, ease: EASE, delay }}
    >
      {children}
    </m.div>
  );
}

/**
 * Homepage proof band for multilingual dictation and consented meeting notes.
 * The illustrated product film is finite, real-DOM UI: no video payload, misspelled raster text,
 * autoplay sound or inaccessible canvas. Reduced motion/global pause render its settled frame.
 */
export default function VoiceAssistantSection({ content = {} }) {
  const c = { ...VOICE_AI_FALLBACK, ...content };
  const cards = Array.isArray(content.cards) && content.cards.length
    ? content.cards
    : VOICE_AI_FALLBACK.cards;
  const prefersReducedMotion = useReducedMotion();
  const { paused: marketingPaused } = useMarketingMotion();
  const reduce = Boolean(prefersReducedMotion || marketingPaused);

  return (
    <section
      id="voice-ai"
      data-testid="voice-ai-section"
      className="campus-glass-surface relative scroll-mt-24 overflow-hidden border-b border-[#d9d3cb] dark:border-white/10"
      aria-labelledby="voice-ai-heading"
    >
      <div className="pointer-events-none absolute -left-24 top-20 h-80 w-80 rounded-full bg-[#eadbc9]/60 blur-3xl dark:bg-[#8c674c]/15" aria-hidden="true" />
      <div className="pointer-events-none absolute -right-28 top-0 h-96 w-96 rounded-full bg-ai/[0.08] blur-3xl dark:bg-ai/15" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-0 opacity-[0.32] [background-image:linear-gradient(rgba(124,58,237,0.09)_1px,transparent_1px),linear-gradient(90deg,rgba(29,78,216,0.08)_1px,transparent_1px)] [background-size:76px_76px] [mask-image:linear-gradient(to_bottom,black,transparent_56%)] dark:opacity-[0.12]" aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-5 py-14 md:py-20 lg:py-24">
        <div className="grid gap-7 lg:grid-cols-[minmax(0,1.05fr)_minmax(300px,0.65fr)] lg:items-end lg:gap-16">
          <FadeUp reduce={reduce} className="max-w-4xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-ai/15 bg-white/65 px-3.5 py-2 text-[12px] font-bold text-ai-ink shadow-sm backdrop-blur-sm dark:border-ai/30 dark:bg-white/[0.06]">
              <Mic className="h-3.5 w-3.5" aria-hidden="true" /> {c.badge}
            </span>
            <h2 id="voice-ai-heading" className="mt-6 max-w-[14ch] font-heading text-[34px] font-extrabold leading-[1] tracking-[-0.04em] text-[#17130f] dark:text-white sm:text-[48px] lg:text-[58px]">
              {c.heading} <span className="text-ai dark:text-[#a78bfa]">{c.highlight}</span>
            </h2>
          </FadeUp>

          <FadeUp reduce={reduce} delay={0.05} className="border-l border-[#ddd4c9] pl-5 dark:border-white/15 sm:pl-7">
            <p className="max-w-[60ch] text-[15px] leading-[1.75] text-[#62584e] dark:text-white/70 sm:text-[16px]">{c.subhead}</p>
            <div className="mt-6 grid grid-cols-3 gap-3 border-t border-[#ddd4c9] pt-4 dark:border-white/15">
              {[
                ["11", "languages"],
                ["33", "audio examples"],
                ["1", "human review"],
              ].map(([value, label]) => (
                <div key={label}>
                  <p className="font-heading text-[19px] font-extrabold tabular-nums text-[#211b16] dark:text-white">{value}</p>
                  <p className="mt-0.5 text-[10px] font-semibold leading-tight text-[#685d52] dark:text-white/55">{label}</p>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>

        <FadeUp reduce={reduce} className="mt-10 sm:mt-12" delay={0.08}>
          <VoiceAiraProductFilm reduce={reduce} testId="voice-demo" />
        </FadeUp>

        <m.div
          className="mt-11 grid grid-cols-1 gap-4 lg:grid-cols-12"
          variants={reduce ? undefined : { hidden: {}, show: { transition: { staggerChildren: 0.065 } } }}
          initial={reduce ? false : "hidden"}
          whileInView={reduce ? undefined : "show"}
          viewport={{ once: true, amount: 0.12 }}
        >
          {cards.map((card, index) => {
            const Icon = CARD_ICONS[index % CARD_ICONS.length];
            const featured = index === 2;
            return (
              <m.article
                key={card.title}
                data-testid={`voice-ai-card-${index}`}
                variants={reduce ? undefined : {
                  hidden: { opacity: 0, transform: "translateY(14px)" },
                  show: { opacity: 1, transform: "translateY(0px)", transition: { duration: 0.38, ease: EASE } },
                }}
                className={`${CARD_LAYOUTS[index % CARD_LAYOUTS.length]} rounded-[24px] border p-5 sm:p-6 ${featured
                  ? "border-[#2a2132] bg-[#17111f] text-white shadow-[0_24px_60px_-42px_rgba(48,26,66,0.82)]"
                  : "border-[#e2d9cf] bg-surface/78 text-[#211b16] backdrop-blur-sm dark:border-white/10 dark:bg-white/[0.05] dark:text-white"}`}
              >
                <span className={`grid h-11 w-11 place-items-center rounded-xl ${featured ? "bg-ai text-white" : "bg-ai/[0.08] text-ai-ink"}`}>
                  <Icon className="h-5 w-5" strokeWidth={2} aria-hidden="true" />
                </span>
                <div className="mt-7 grid gap-2 sm:grid-cols-[minmax(140px,0.68fr)_minmax(0,1.32fr)] sm:gap-6">
                  <h3 className="font-heading text-[17px] font-bold leading-snug">{card.title}</h3>
                  <p className={`text-[12.5px] leading-[1.68] ${featured ? "text-white/68" : "text-[#665c52] dark:text-white/68"}`}>{card.body}</p>
                </div>
              </m.article>
            );
          })}
        </m.div>

        <FadeUp reduce={reduce} className="mt-10 grid gap-5 border-t border-[#ddd4c9] pt-7 dark:border-white/15 lg:grid-cols-[auto_minmax(0,1fr)] lg:items-center" delay={0.04}>
          <a
            href="/modules/voice-aira"
            data-testid="voice-ai-cta"
            className="group inline-flex min-h-12 w-fit items-center gap-2 rounded-full bg-primary px-6 text-sm font-semibold text-primary-fg shadow-[0_10px_24px_rgba(29,78,216,0.22)] transition-[background-color,transform,box-shadow] duration-200 hover:-translate-y-px hover:bg-primary-700 hover:shadow-[0_14px_30px_rgba(29,78,216,0.28)] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
          >
            See Voice Aira in action
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" />
          </a>
          <p className="max-w-2xl text-[12px] leading-[1.65] text-[#6f6358] dark:text-white/60 lg:justify-self-end lg:text-right">{c.footnote}</p>
        </FadeUp>
      </div>
    </section>
  );
}
